import { ArrowBack, ArrowForward, Close } from '@mui/icons-material';
import {
  Box,
  Grid,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import debounce from 'lodash.debounce';
import React, { cloneElement, useCallback, useEffect, useState } from 'react';
import {
  checkOverFlow,
  getActivePositionOffSetLeft,
  getHorizontalCenterPositionLeftBased,
} from '../helpers/overflow';
import GenButton from './GenButton';
import GenSummary from './GenSummary';

const StepperWizard = ({
  steps,
  disabled = false,
  loading = false,
  onCancelFirstStep,
  onNext,
  currentStep,
  defaultButtons = {
    position: 'flex-end',
    columns: 12,
    prev: {
      text: 'Back',
      icon: <ArrowBack />,
      variant: 'contained',
      color: 'error',
    },
    next: {
      default: {
        text: 'Next',
        icon: <ArrowForward />,
        variant: 'contained',
        color: 'primary',
      },
      isFinalStep: {
        text: 'End',
        icon: <Close />,
        variant: 'contained',
        color: 'success',
        function: () => {},
      },
    },
  },
}) => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (currentStep === undefined || currentStep === null) return;
    setActiveStep(currentStep);
  }, [currentStep]);

  const handleNextStep = () => {
    const canGoNext = onNext && onNext(activeStep);
    if (onNext && canGoNext === false) return;
    setActiveStep(state => state + 1);
  };

  const handleBackStep = () => {
    setActiveStep(state => state - 1);
  };

  const resetStepper = () => {
    setActiveStep(0);
  };

  const isEndStep = steps.length - 1 === activeStep;

  const nextAndFinalStepHandler = () => {
    return isEndStep
      ? defaultButtons.next.isFinalStep
      : defaultButtons.next.default;
  };

  const handleNextAndFinalStepFunction = e => {
    if (steps[activeStep].formId) return;

    e.stopPropagation();
    e.preventDefault();

    if (!isEndStep) {
      handleNextStep();
      return;
    }

    (defaultButtons.next.isFinalStep.function || resetStepper)();
  };

  const currentStepHasActiveSummary = () => {
    return steps[activeStep]?.summary ? true : false;
  };

  const getButtons = (
    spacing = 3,
    justifyContent = defaultButtons.position
  ) => {
    const { buttons, formId } = steps[activeStep];

    const getPreviousButton = () => {
      if (activeStep !== 0)
        return (
          <Grid item pr={spacing}>
            <GenButton
              text={defaultButtons.prev?.text}
              startIcon={defaultButtons.prev?.icon}
              color={defaultButtons.prev?.color}
              variant={defaultButtons.prev?.variant}
              sx={defaultButtons.prev?.sx}
              onClick={handleBackStep}
            />
          </Grid>
        );

      if (activeStep === 0 && onCancelFirstStep)
        return (
          <Grid item pr={spacing}>
            <GenButton
              text={defaultButtons.cancel?.text}
              startIcon={defaultButtons.cancel?.icon}
              color={defaultButtons.cancel?.color}
              variant={defaultButtons.cancel?.variant}
              sx={defaultButtons.cancel?.sx}
              onClick={() => onCancelFirstStep()}
            />
          </Grid>
        );

      return null;
    };

    const getGenButtonType = () => {
      return formId ? 'submit' : null;
    };

    const getGenButtonFormName = () => {
      return formId ? formId : null;
    };

    return buttons ? (
      <Grid container direction="row" justifyContent="center">
        <Grid item xs={buttons.columns}>
          <Grid container justifyContent={buttons.position} spacing={spacing}>
            {Object.entries(buttons.components).map(
              ([key, button], indexButton) => (
                <Grid
                  key={indexButton}
                  item
                  onClick={() => (key === 'prev' && handleBackStep()) || true}
                  order={button.order}
                >
                  {isEndStep
                    ? cloneElement(button.component, {
                        reset: resetStepper,
                      })
                    : button.component}
                </Grid>
              )
            )}
          </Grid>
        </Grid>
      </Grid>
    ) : (
      <Grid container direction="row" justifyContent="center">
        <Grid item xs={defaultButtons.columns} display="flex" padding={spacing}>
          <Grid
            container
            justifyContent={justifyContent}
            alignItems="center"
            flexDirection="row-reverse"
          >
            <Grid item>
              <GenButton
                text={nextAndFinalStepHandler().text}
                endIcon={nextAndFinalStepHandler().icon}
                color={nextAndFinalStepHandler().color}
                variant={nextAndFinalStepHandler().variant}
                sx={nextAndFinalStepHandler().sx}
                type={getGenButtonType()}
                form={getGenButtonFormName()}
                disabled={disabled}
                loading={loading}
                onClick={handleNextAndFinalStepFunction}
              />
            </Grid>
            {getPreviousButton()}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const [element, setElementRect] = useState(null);
  const stepper = useCallback(node => {
    setElementRect(node);
  }, []);

  const [overFlow, setOverFlow] = useState(false);
  const [position, setPosition] = useState(0);

  useEffect(() => {
    const debouncedHandleResize = debounce(() => {
      if (element) {
        setOverFlow(checkOverFlow(element));
        setPosition(
          getHorizontalCenterPositionLeftBased(
            getActivePositionOffSetLeft(element)
          )
        );
      }
    }, 500);

    window.addEventListener('resize', debouncedHandleResize);

    return _ => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  });

  useEffect(() => {
    if (element) {
      setOverFlow(checkOverFlow(element));
    }
  }, [element]);

  useEffect(() => {
    if (overFlow) {
      setPosition(
        getHorizontalCenterPositionLeftBased(
          getActivePositionOffSetLeft(element)
        )
      );
    }
  }, [overFlow, element, activeStep]);

  const theme = useTheme();
  const isUpMediumBreakPoint = useMediaQuery(theme.breakpoints.down('md'));

  const summaryColumnSize = breakPoint => (breakPoint ? 3 : 12);

  const stepHasActiveSummary = summary =>
    summary ? (
      <Grid
        item
        xs={summaryColumnSize(!isUpMediumBreakPoint)}
        sx={{
          ...(isUpMediumBreakPoint && { flexBasis: { sm: 'unset' } }),
        }}
      >
        <GenSummary
          {...summary}
          children={
            steps[activeStep]?.buttonsIntoSummary &&
            getButtons(0, 'space-between')
          }
        />
      </Grid>
    ) : null;

  return (
    <Grid
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="stretch"
      sx={{ height: '100%' }}
      wrap="nowrap"
    >
      <Grid item>
        <Box
          sx={{
            position: 'relative',
            borderBottom: 1,
            borderColor: theme.palette.divider,
            bgcolor: 'background.paper',
            overflow: 'hidden',
            p: { xs: 1, md: 4 },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              height: '100%',
              width: '20px',
              background: 'linear-gradient(to left , #fff, rgba(0,0,0,0))',
              zIndex: 1,
            }}
          ></Box>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '20px',
              background: 'linear-gradient(to left , rgba(0,0,0,0), #fff )',
              zIndex: 1,
            }}
          ></Box>
          <Stepper
            ref={stepper}
            activeStep={activeStep}
            sx={{
              left: `${overFlow ? position : 0}px`,
              position: 'relative',
              justifyContent: `${overFlow ? 'flex-start' : 'center'}`,
              transition: 'all 1s',
            }}
            connector={<StepConnector sx={{ minWidth: '32px' }} />}
          >
            {steps.map(s => {
              return (
                <Step
                  key={s.label}
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  <StepLabel
                    sx={{ flexDirection: `${overFlow ? 'column' : 'row'}` }}
                  >
                    {s.label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
      </Grid>
      <Grid item sx={{ flexGrow: 1 }}>
        <Grid container direction="row" sx={{ height: '100%' }}>
          <Grid
            item
            xs={currentStepHasActiveSummary() && !isUpMediumBreakPoint ? 9 : 12}
            sx={
              currentStepHasActiveSummary()
                ? { maxHeight: 'calc(100vh - 194px)', overflow: 'scroll' }
                : null
            }
          >
            <Grid
              container
              direction="column"
              sx={{ height: '100%', flexWrap: 'nowrap' }}
            >
              <Grid
                item
                sx={
                  steps[activeStep]?.buttonsIntoSummary && { flex: '1 1 auto' }
                }
              >
                {cloneElement(steps[activeStep]?.component, {
                  handleNextStep,
                })}
              </Grid>
              {!steps[activeStep]?.buttonsIntoSummary && (
                <Grid item sx={{ flex: '1 1 auto' }}>
                  {getButtons()}
                </Grid>
              )}
            </Grid>
          </Grid>
          {stepHasActiveSummary(steps[activeStep]?.summary)}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default StepperWizard;
